import { clearAuth, setShowLoginModal, setAccessToken, setRefreshToken } from '../slices/authSlice';
import { requestRefresh } from './refreshGate';

const IS_WEB = typeof window !== 'undefined' && window.electron && !window.electron.db;

function getToken(name) {
    // Web: from Redux memory (no localStorage). Electron: from localStorage.
    if (IS_WEB) {
        const store = window.__reduxStore;
        const auth = store?.getState?.().auth;
        return name === 'access' ? auth?.accessToken : auth?.refreshToken;
    }
    return localStorage.getItem(name === 'access' ? 'auth:accessToken' : 'auth:refreshToken');
}

/**
 * The app's one dead-session path: the refresh token was rejected, so no
 * call can succeed until the user signs in again. Clears tokens and opens the
 * sign-in modal — never a message telling the user to go do that themselves.
 */
export function forceLogout() {
    if (!IS_WEB) {
        localStorage.removeItem('auth:accessToken');
        localStorage.removeItem('auth:refreshToken');
    }
    const store = window.__reduxStore;
    if (store) {
        store.dispatch(clearAuth());
        store.dispatch(setShowLoginModal(true));
    }
}

export async function refreshAndRetry(apiCall) {
    let token = getToken('access');
    if (!token) return { error: 'Not authenticated' };

    let result = await apiCall(token);

    // If auth failed, try refreshing the token.
    // Handle both { error: string } (auth handlers) and { error: { code, message } } (library handlers).
    const errorCode = result.error?.code;
    const errorStr = typeof result.error === 'string' ? result.error : (result.error?.message || '');
    if (result.error && (
        errorCode === 'NOT_AUTHENTICATED' ||
        errorStr.includes('expired') ||
        errorStr.includes('Invalid') ||
        errorStr.includes('authentication')
    )) {
        const refreshToken = getToken('refresh');
        if (!refreshToken) {
            forceLogout();
            return { error: 'Not authenticated' };
        }

        // Single-flight + 429 cooldown live in the gate: concurrent 401s share
        // one refresh, and a rate-limited refresh fails this call fast instead
        // of spending another attempt against the limit.
        const refreshResponse = await requestRefresh(refreshToken);

        if (refreshResponse.accessToken) {
            token = refreshResponse.accessToken;
            const store = window.__reduxStore;
            if (IS_WEB) {
                store?.dispatch?.(setAccessToken(token));
                if (refreshResponse.refreshToken) {
                    store?.dispatch?.(setRefreshToken(refreshResponse.refreshToken));
                }
            } else {
                localStorage.setItem('auth:accessToken', token);
                store?.dispatch?.(setAccessToken(token));
                if (refreshResponse.refreshToken) {
                    // Rotation: the token just used is dying; keep the new one.
                    localStorage.setItem('auth:refreshToken', refreshResponse.refreshToken);
                    store?.dispatch?.(setRefreshToken(refreshResponse.refreshToken));
                }
            }
            result = await apiCall(token);
        } else if (refreshResponse.status === 401) {
            // Refresh token truly rejected (expired/revoked) — logout
            console.warn('Refresh token rejected:', refreshResponse.error);
            forceLogout();
            return { error: 'Session expired. Please log in again.' };
        } else {
            // Network error or server issue — don't logout, just fail this request
            console.warn('Token refresh failed (transient):', refreshResponse.error);
            return result;
        }
    }

    return result;
}
